import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { BRAND_GREEN } from "@/lib/design-tokens";

export interface TourStep {
  selector: string;
  titleKey: string;
  bodyKey: string;
  placement: "top" | "bottom" | "left" | "right";
}

interface HostTourOverlayProps {
  open: boolean;
  steps: TourStep[];
  stepIndex: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onFinish: () => void;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8;
const POPOVER_GAP = 12;
const TRANSITION_MS = 250;

function getElementRect(selector: string): Rect | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    top: r.top + window.scrollY,
    left: r.left + window.scrollX,
    width: r.width,
    height: r.height,
  };
}

function scrollToElement(selector: string): void {
  const el = document.querySelector(selector);
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const isInView =
    rect.top >= 0 &&
    rect.bottom <= window.innerHeight &&
    rect.left >= 0 &&
    rect.right <= window.innerWidth;
  if (!isInView) {
    el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  }
}

export function HostTourOverlay({
  open,
  steps,
  stepIndex,
  onNext,
  onPrev,
  onSkip,
  onFinish,
}: HostTourOverlayProps) {
  const { t } = useAppTranslation();
  const [cutout, setCutout] = useState<Rect | null>(null);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const [visible, setVisible] = useState(false);

  const currentStep = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  useEffect(() => {
    if (!open || !currentStep) return;
    const el = document.querySelector(currentStep.selector);
    if (!el) {
      if (isLast) {
        onFinish();
      } else {
        onNext();
      }
    }
  }, [open, stepIndex, currentStep, isLast, onNext, onFinish]);

  const positionOverlay = useCallback(() => {
    if (!currentStep) return;
    const rect = getElementRect(currentStep.selector);
    if (!rect) {
      setCutout(null);
      setPopoverStyle({ top: "50%", left: "50%", transform: "translate(-50%, -50%)" });
      return;
    }

    setCutout(rect);

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const isMobile = vw < 768;
    const popoverWidth = Math.min(360, vw - 32);

    let top = 0;
    let left = 0;
    const placement = currentStep.placement;

    const screenRect = {
      top: rect.top - window.scrollY,
      left: rect.left - window.scrollX,
    };

    if (isMobile || placement === "bottom") {
      top = screenRect.top + rect.height + PADDING + POPOVER_GAP;
      left = Math.max(16, Math.min(screenRect.left, vw - popoverWidth - 16));
      if (top + 200 > vh) {
        top = screenRect.top - PADDING - POPOVER_GAP - 200;
      }
    } else if (placement === "top") {
      top = screenRect.top - PADDING - POPOVER_GAP - 200;
      left = Math.max(16, Math.min(screenRect.left, vw - popoverWidth - 16));
      if (top < 16) {
        top = screenRect.top + rect.height + PADDING + POPOVER_GAP;
      }
    } else if (placement === "right") {
      top = screenRect.top;
      left = screenRect.left + rect.width + PADDING + POPOVER_GAP;
      if (left + popoverWidth > vw - 16) {
        left = screenRect.left - PADDING - POPOVER_GAP - popoverWidth;
      }
    } else {
      top = screenRect.top;
      left = screenRect.left - PADDING - POPOVER_GAP - popoverWidth;
      if (left < 16) {
        left = screenRect.left + rect.width + PADDING + POPOVER_GAP;
      }
    }

    top = Math.max(16, Math.min(top, vh - 220));
    left = Math.max(16, Math.min(left, vw - popoverWidth - 16));

    setPopoverStyle({
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`,
      width: `${popoverWidth}px`,
    });
  }, [currentStep]);

  useEffect(() => {
    if (!open || !currentStep) return;
    scrollToElement(currentStep.selector);
    const timer = setTimeout(() => {
      positionOverlay();
      setVisible(true);
    }, 50);
    return () => clearTimeout(timer);
  }, [open, stepIndex, currentStep, positionOverlay]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => positionOverlay();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open, positionOverlay]);

  useEffect(() => {
    if (!open) {
      setVisible(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onSkip();
      if (e.key === "ArrowRight") isLast ? onFinish() : onNext();
      if (e.key === "ArrowLeft" && stepIndex > 0) onPrev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onSkip, onNext, onPrev, onFinish, isLast, stepIndex]);

  if (!open || !currentStep) return null;

  const screenCutout = cutout
    ? {
        top: cutout.top - window.scrollY - PADDING,
        left: cutout.left - window.scrollX - PADDING,
        width: cutout.width + PADDING * 2,
        height: cutout.height + PADDING * 2,
      }
    : null;

  return (
    <div className="fixed inset-0 z-[200]" data-testid="host-tour-overlay">
      <svg
        className="absolute inset-0 h-full w-full"
        style={{ transition: `all ${TRANSITION_MS}ms ease-in-out` }}
      >
        <defs>
          <mask id="tour-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {screenCutout && (
              <rect
                x={screenCutout.left}
                y={screenCutout.top}
                width={screenCutout.width}
                height={screenCutout.height}
                rx="12"
                fill="black"
                style={{ transition: `all ${TRANSITION_MS}ms ease-in-out` }}
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.65)"
          mask="url(#tour-spotlight-mask)"
        />
      </svg>

      <div
        className="absolute z-[201]"
        style={{
          ...popoverStyle,
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(8px)",
          transition: `opacity ${TRANSITION_MS}ms ease, transform ${TRANSITION_MS}ms ease`,
        }}
        data-testid="tour-popover"
      >
        <div className="rounded-xl bg-white p-5 shadow-2xl ring-1 ring-black/5">
          <div className="mb-1 flex items-start justify-between gap-2">
            <h3 className="font-sans text-base font-bold text-gray-900">
              {t(currentStep.titleKey)}
            </h3>
            <button
              type="button"
              onClick={onSkip}
              className="cursor-pointer -mr-1 -mt-1 rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              aria-label={t("common.close")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <p className="text-sm leading-relaxed text-gray-600">
            {t(currentStep.bodyKey)}
          </p>

          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-400">
              {t("host_tour.step_counter", {
                current: stepIndex + 1,
                total: steps.length,
              })}
            </span>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onSkip}
                className="text-xs font-medium text-gray-400 transition hover:text-gray-600"
                data-testid="tour-skip-step-btn"
              >
                {t("host_tour.cta_skip")}
              </button>
              {stepIndex > 0 && (
                <button
                  type="button"
                  onClick={onPrev}
                  className="cursor-pointer inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  {t("host_tour.back")}
                </button>
              )}
              <button
                type="button"
                onClick={isLast ? onFinish : onNext}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90"
                style={{ backgroundColor: BRAND_GREEN }}
                data-testid="tour-next-btn"
              >
                {isLast ? t("host_tour.finish") : t("host_tour.next")}
                {!isLast && <ChevronRight className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
